import {
  Carriers,
  UpsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import {
  CarrierAndService,
  getAllCarrierServiceCombinations,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { DynamicRatePricingEnabledCarriers } from "@redotech/redo-fulfillment-carrier-common/dynamic-rate-pricing";
import type { DynamicRatePricingRuleValue } from "./dynamic-rate-pricing-settings-form";

export function serviceKey({ carrier, service }: CarrierAndService): string {
  return `${carrier}:${service}`;
}

export interface PricingServiceGroup {
  key: string;
  carrier: Carriers;
  label: string;
  services: CarrierAndService[];
}

const serviceCatalog = getAllCarrierServiceCombinations();

function groupLabel(source: CarrierAndService): string {
  if (source.carrier === Carriers.GOFO) {
    const level = Object.entries({
      STD: "Standard",
      REG: "Regional",
      EXP: "Expedited",
      ECO: "Economy",
    }).find(([prefix]) => source.service.startsWith(prefix))?.[1];
    if (level) {
      return `GOFO ${level}`;
    }
  }
  return getCarrierServiceLabel(source.carrier, source.service);
}

export const pricingServiceGroups: PricingServiceGroup[] = [];
for (const source of serviceCatalog) {
  if (!DynamicRatePricingEnabledCarriers.safeParse(source.carrier).success) {
    continue;
  }
  const label = groupLabel(source);
  const key = `${source.carrier}:${label}`;
  const existing = pricingServiceGroups.find((group) => group.key === key);
  if (existing) {
    existing.services.push(source);
  } else {
    pricingServiceGroups.push({
      key,
      carrier: source.carrier,
      label,
      services: [source],
    });
  }
}

export function rulesForGroup(
  group: PricingServiceGroup,
  rules: DynamicRatePricingRuleValue[],
) {
  return rules.filter((rule) =>
    group.services.some(
      (source) => serviceKey(source) === serviceKey(rule.source),
    ),
  );
}

export function updateGroupRules(
  rules: DynamicRatePricingRuleValue[],
  group: PricingServiceGroup,
  value: DynamicRatePricingRuleValue | undefined,
): DynamicRatePricingRuleValue[] {
  return [
    ...rules.filter(
      (rule) =>
        !group.services.some(
          (source) => serviceKey(source) === serviceKey(rule.source),
        ),
    ),
    ...(value ? group.services.map((source) => ({ ...value, source })) : []),
  ];
}

export function hasMixedGroupRules(
  group: PricingServiceGroup,
  rules: DynamicRatePricingRuleValue[],
): boolean {
  const matches = rulesForGroup(group, rules);
  const signature = (rule: DynamicRatePricingRuleValue) =>
    JSON.stringify([
      groupBenchmarkServices(rule.benchmarks)
        .map((group) => group.key)
        .sort(),
      rule.minBeatRateBy,
      rule.maxBeatRateBy,
    ]);
  return (
    matches.length > 0 &&
    (matches.length !== group.services.length ||
      new Set(matches.map(signature)).size > 1)
  );
}

export function groupBenchmarkServices(services: CarrierAndService[]) {
  const groups = new Map<
    string,
    { key: string; source: CarrierAndService; services: CarrierAndService[] }
  >();
  for (const source of services) {
    const isGroundSaver =
      (source.carrier === Carriers.UPS ||
        source.carrier === Carriers.UPS_SUREPOST) &&
      (source.service === UpsServices.SurePostUnder1Lb ||
        source.service === UpsServices.SurePostOver1Lb);
    const key = isGroundSaver ? "ups-ground-saver" : serviceKey(source);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        source,
        services: isGroundSaver
          ? [
              {
                carrier: Carriers.UPS_SUREPOST,
                service: UpsServices.SurePostUnder1Lb,
              },
              {
                carrier: Carriers.UPS_SUREPOST,
                service: UpsServices.SurePostOver1Lb,
              },
            ]
          : [source],
      });
    }
  }
  return [...groups.values()];
}
